if (!customElements.get('product-quick-add-drawer')) {
  customElements.define('product-quick-add-drawer', class ProductQuickAddDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.closeBtn = this.querySelector('[data-drawer-close]');
      this.titleEl = this.querySelector('[data-drawer-title]');
      this.imageEl = this.querySelector('[data-drawer-image]');
      this.flavourSection = this.querySelector('[data-drawer-flavour-section]');
      this.purchaseSection = this.querySelector('[data-drawer-purchase-section]');
      this.atcBtn = this.querySelector('[data-drawer-atc]');
      this.previewLink = this.querySelector('[data-drawer-preview]');
      this.errorEl = this.querySelector('[data-drawer-error]');

      this.wishlistBtn = this.querySelector('[data-drawer-wishlist]');

      this.currentProduct = null;
      this.currentVariant = null;
      this.currentSellingPlan = null;
      this.flavourProducts = [];

      this.overlay?.addEventListener('click', () => this.close());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.atcBtn?.addEventListener('click', () => this.addToCart());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('active')) this.close();
      });
    }

    open(data) {
      this.resetState();

      this.titleEl.textContent = data.title || '';
      if (data.imageUrl) {
        this.imageEl.src = data.imageUrl;
        this.imageEl.alt = data.title || '';
        this.imageEl.closest('.product-quick-add-drawer__image-wrapper').style.display = '';
      }
      if (data.productUrl) {
        this.previewLink.href = data.productUrl;
      }

      this.updateAtcPrice(data.price);

      this.showSkeletons();
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('quick-add-drawer-open');

      this.fetchProductData(data.handle, data.setHandle);
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('quick-add-drawer-open');
    }

    resetState() {
      this.currentProduct = null;
      this.currentVariant = null;
      this.currentSellingPlan = null;
      this.skioProduct = null;
      this.flavourProducts = [];
      this.hideError();
      this.flavourSection.innerHTML = '';
      this.purchaseSection.innerHTML = '';
      this.atcBtn.disabled = false;
      this.atcBtn.classList.remove('loading');
      if (this.wishlistBtn && typeof this.wishlistBtn.reset === 'function') {
        this.wishlistBtn.reset();
      }
    }

    showSkeletons() {
      this.flavourSection.innerHTML = '<div class="product-quick-add-drawer__skeleton product-quick-add-drawer__skeleton--flavour"></div>';
      this.purchaseSection.innerHTML = '<div class="product-quick-add-drawer__skeleton product-quick-add-drawer__skeleton--purchase"></div>';
    }

    async fetchProductData(handle, setHandle) {
      try {
        const requests = [
          fetch(`/products/${handle}.json`),
          fetch(`/products/${handle}.js`),
        ];
        if (setHandle) {
          requests.push(fetch(`/collections/${setHandle}/products.json?limit=250`));
        }

        const responses = await Promise.all(requests);
        const productJson = await responses[0].json();
        this.currentProduct = productJson.product;
        this.currentVariant = this.currentProduct.variants[0];

        this.skioProduct = await responses[1].json();

        if (responses[2]) {
          const collectionJson = await responses[2].json();
          this.flavourProducts = (collectionJson.products || []).sort((a, b) =>
            a.title.localeCompare(b.title)
          );
        }

        this.renderFlavours();
        this.renderPurchaseType();
        this.updateAtcFromProduct();
        this.updateImage();
        this.updateDrawerWishlistButton();
      } catch (err) {
        console.error('ProductQuickAddDrawer: failed to fetch product data', err);
        this.showError('Failed to load product. Please try again.');
        this.flavourSection.innerHTML = '';
        this.purchaseSection.innerHTML = '';
      }
    }

    updateImage() {
      if (this.currentProduct?.image?.src) {
        this.imageEl.src = this.currentProduct.image.src;
        this.imageEl.alt = this.currentProduct.title || '';
      }
    }

    /* ---- Flavour dropdown ---- */

    renderFlavours() {
      if (!this.flavourProducts.length || this.flavourProducts.length < 2) {
        this.flavourSection.innerHTML = '';
        return;
      }

      const count = this.flavourProducts.length;
      const currentSubtitle = this.getSubtitle(this.currentProduct.title);
      const currentImage = this.getFlavourImage(this.currentProduct);

      let optionsHtml = '';
      const sorted = [...this.flavourProducts].sort((a, b) => a.title.localeCompare(b.title));
      for (const fp of sorted) {
        if (fp.handle === this.currentProduct.handle) continue;
        const subtitle = this.getSubtitle(fp.title);
        const img = this.getFlavourImage(fp);
        optionsHtml += `
          <div class="product-quick-add-drawer__flavour-option" data-handle="${fp.handle}">
            <img src="${img}" alt="${this.escapeHtml(subtitle)}" loading="lazy">
            <span>${this.escapeHtml(subtitle)}</span>
          </div>`;
      }

      this.flavourSection.innerHTML = `
        <p class="product-quick-add-drawer__section-label">Available Flavours (${count})</p>
        <div class="product-quick-add-drawer__flavour-select">
          <div class="product-quick-add-drawer__flavour-selected" data-flavour-toggle>
            <img src="${currentImage}" alt="${this.escapeHtml(currentSubtitle)}">
            <span>${this.escapeHtml(currentSubtitle)}</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="product-quick-add-drawer__flavour-list">${optionsHtml}</div>
        </div>`;

      const toggle = this.flavourSection.querySelector('[data-flavour-toggle]');
      toggle?.addEventListener('click', () => toggle.classList.toggle('active'));

      this.flavourSection.querySelectorAll('.product-quick-add-drawer__flavour-option').forEach(opt => {
        opt.addEventListener('click', () => this.onFlavourChange(opt.dataset.handle));
      });
    }

    async onFlavourChange(handle) {
      this.showSkeletons();

      try {
        const [jsonRes, jsRes] = await Promise.all([
          fetch(`/products/${handle}.json`),
          fetch(`/products/${handle}.js`),
        ]);
        const json = await jsonRes.json();
        this.currentProduct = json.product;
        this.currentVariant = this.currentProduct.variants[0];
        this.currentSellingPlan = null;

        this.skioProduct = await jsRes.json();

        this.renderFlavours();
        this.renderPurchaseType();
        this.updateAtcFromProduct();
        this.updateImage();
        this.updateDrawerWishlistButton();

        const title = this.currentProduct.title.split(' - ')[0].trim();
        this.titleEl.textContent = title;

        const productUrl = this.previewLink.href;
        if (productUrl) {
          const url = new URL(productUrl, window.location.origin);
          const pathParts = url.pathname.split('/');
          pathParts[pathParts.length - 1] = handle;
          url.pathname = pathParts.join('/');
          this.previewLink.href = url.pathname;
        }
      } catch (err) {
        console.error('ProductQuickAddDrawer: failed to switch flavour', err);
        this.showError('Failed to load flavour. Please try again.');
      }
    }

    /* ---- Purchase Type (Skio plan picker) ---- */

    renderPurchaseType() {
      if (!this.currentProduct) {
        this.purchaseSection.innerHTML = '';
        return;
      }

      const hasSellingPlans = this.skioProduct?.selling_plan_groups?.length > 0;

      if (!hasSellingPlans) {
        const price = this.skioProduct?.variants?.[0]?.price;
        const formatted = price ? this.formatMoney(price) : '';
        this.purchaseSection.innerHTML = `
          <p class="product-quick-add-drawer__section-label">Purchase Type</p>
          <div class="product-quick-add-drawer__onetime">
            <label class="product-quick-add-drawer__onetime-label">
              <input type="radio" checked>
              <span>One time purchase</span>
            </label>
            <span class="product-quick-add-drawer__onetime-price">${formatted}</span>
          </div>`;
        return;
      }

      const options = { ...(window.skioDrawerOptions || {}), form_id: 'quick-add-drawer-form' };
      const moneyFormat = window.skioDrawerMoneyFormat || window.money_format || '${{amount}}';

      const escAttr = (val) => String(val).replace(/&/g, '&amp;').replace(/'/g, '&#39;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      const optionsJson = escAttr(JSON.stringify(options));
      const layout = options.layout || 'vertical';
      const handle = this.currentProduct.handle;

      const variantId = this.currentVariant?.id || '';

      const productAttr = this.skioProduct ? escAttr(JSON.stringify(this.skioProduct)) : '';
      const variantAttr = this.skioProduct ? escAttr(JSON.stringify(this.skioProduct.variants[0])) : '';

      this.purchaseSection.innerHTML = `
        <p class="product-quick-add-drawer__section-label">Purchase Type</p>
        <form id="quick-add-drawer-form" action="/cart/add" method="post" style="display:contents;">
          <input type="hidden" name="id" value="${variantId}">
          <skio-plan-picker
            key="quick-add-drawer-${handle}"
            context="drawer"
            options="${optionsJson}"
            layout="${layout}"
            moneyFormat="${escAttr(moneyFormat)}"
            ${productAttr ? `product="${productAttr}"` : `productHandle="${handle}"`}
            ${variantAttr ? `selectedVariant="${variantAttr}"` : ''}
          >
            <input type="hidden" name="selling_plan">
          </skio-plan-picker>
        </form>`;

      this.ensureSkioScript();

      this.observeSkioChanges();
    }

    ensureSkioScript() {
      if (document.querySelector('script[src*="skio-plan-picker-component"]')) return;
      const script = document.createElement('script');
      script.src = window.skioScriptUrl || '';
      script.type = 'module';
      if (script.src) document.head.appendChild(script);
    }

    observeSkioChanges() {
      const picker = this.purchaseSection.querySelector('skio-plan-picker');
      if (!picker) return;

      picker.addEventListener('skio::update-selling-plan', (e) => {
        const { variant, sellingPlan } = e.detail || {};
        this.currentSellingPlan = sellingPlan?.id || null;

        if (!variant) {
          this.updateAtcFromProduct();
          return;
        }

        if (!sellingPlan) {
          this.updateAtcPrice(variant.price);
          return;
        }

        const adj = sellingPlan.price_adjustments?.[0];
        if (!adj) {
          this.updateAtcPrice(variant.price);
          return;
        }

        const price = variant.price;
        let discountAmount = 0;

        switch (adj.value_type) {
          case 'percentage':
            discountAmount = Math.round((price * adj.value) / 100);
            break;
          case 'fixed_amount':
            discountAmount = adj.value;
            break;
          case 'price':
            discountAmount = price - adj.value;
            break;
        }

        this.updateAtcPrice(price - discountAmount);
      });
    }

    /* ---- Pricing ---- */

    updateAtcFromProduct() {
      const jsVariant = this.skioProduct?.variants?.[0];
      if (jsVariant) {
        this.updateAtcPrice(jsVariant.price);
        return;
      }
      if (!this.currentVariant) return;
      this.updateAtcPrice(Math.round(parseFloat(this.currentVariant.price) * 100));
    }

    updateAtcPrice(priceInCents) {
      if (priceInCents == null) return;
      const cents = typeof priceInCents === 'string' ? parseFloat(priceInCents) : priceInCents;
      const formatted = this.formatMoney(cents);
      this.atcBtn.textContent = `Add To Cart – ${formatted}`;
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return `$${amount}`;
    }

    /* ---- Add to Cart ---- */

    async addToCart() {
      if (!this.currentVariant) return;
      if (this.atcBtn.classList.contains('loading')) return;

      this.hideError();
      this.atcBtn.classList.add('loading');
      this.atcBtn.disabled = true;

      const sellingPlanInput = this.purchaseSection.querySelector('input[name="selling_plan"]');
      const sellingPlan = sellingPlanInput?.value || this.currentSellingPlan || null;

      const body = {
        id: this.currentVariant.id,
        quantity: 1,
        sections: this.getSectionsToRender().map(s => s.section),
        sections_url: window.location.pathname,
      };

      if (sellingPlan) {
        body.selling_plan = sellingPlan;
      }

      try {
        const config = typeof fetchConfig === 'function' ? fetchConfig() : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        };
        config.body = JSON.stringify(body);

        const response = await fetch(window.routes.cart_add_url, config);
        const json = await response.json();

        if (!response.ok || json.status) {
          throw new Error(json.description || json.message || 'Failed to add to cart');
        }

        try { this.renderSections(json); } catch (e) { console.warn('ProductQuickAddDrawer: renderSections failed', e); }

        if (typeof window.refreshCartFromServer === 'function') {
          await window.refreshCartFromServer();
        } else if (typeof window.handleCartUpdated === 'function') {
          window.handleCartUpdated(json);
        } else {
          window.cartJSON = json;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-quick-add-drawer',
            cartData: json,
            productVariantId: this.currentVariant.id,
          });
        }

        if (typeof window.runAutoGift === 'function') window.runAutoGift();
        if (typeof window.runAutoDonation === 'function') window.runAutoDonation();

        this.close();

        try {
          const cartNotification = document.querySelector('cart-notification');
          if (cartNotification) cartNotification.renderContents(json);
        } catch (e) { console.warn('ProductQuickAddDrawer: cart notification update failed', e); }
      } catch (err) {
        console.error('ProductQuickAddDrawer: add to cart failed', err);
        this.showError(err.message || 'Failed to add to cart. Please try again.');
      } finally {
        this.atcBtn.classList.remove('loading');
        this.atcBtn.disabled = false;
      }
    }

    getSectionsToRender() {
      return [
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-notifications-wrapper', section: 'cart-notifications' },
        { id: 'cart-notification-products', section: 'cart-notification-products' },
        { id: 'mini-cart-upsell', section: 'mini-cart-upsell' },
      ];
    }

    renderSections(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (!el || !parsedState.sections?.[section.section]) return;

        if (section.section === 'cart-drawer') {
          el.closest('cart-drawer')?.classList.remove('is-empty');
        }

        const target = section.selector ? el.querySelector(section.selector) || el : el;
        target.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });
    }

    getSectionInnerHTML(html, selector) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return selector ? (doc.querySelector(selector)?.innerHTML || '') : html;
    }

    /* ---- Helpers ---- */

    getSubtitle(title) {
      const parts = (title || '').split(' - ');
      return parts.length > 1 ? parts.slice(1).join(' - ') : parts[0];
    }

    getFlavourImage(product) {
      if (product.metafields?.custom?.flavour_image) {
        return product.metafields.custom.flavour_image;
      }
      return product.image?.src || product.images?.[0]?.src || '';
    }

    updateDrawerWishlistButton() {
      if (!this.wishlistBtn || !this.currentProduct) return;
      const variant = this.currentVariant || this.currentProduct.variants[0];
      this.wishlistBtn.setProduct({
        productId: this.currentProduct.id,
        productTitle: this.currentProduct.title,
        productUrl: this.previewLink.href || `/products/${this.currentProduct.handle}`,
        imageUrl: this.currentProduct.image?.src || '',
        variant: variant,
      });
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    showError(msg) {
      if (this.errorEl) {
        this.errorEl.textContent = msg;
        this.errorEl.classList.add('visible');
      }
    }

    hideError() {
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('visible');
      }
    }
  });
}
